import { SettingsIcon } from "components/Icons";
import { uniqBy } from "lodash";
import React, {
  ReactElement,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { CancellableAPIRequest } from "restAPI";
import { AnyaSessionMode } from "types/anya";
import { AnyaAgentSummary } from "views/Automations/AnyaAgents/types";

import ACAsyncSearchList, {
  ACAsyncSearchListState,
} from "ac-components/ACAsyncSearchList";
import TickIcon from "ac-components/ac-icons/TickIcon";
import ACMiddleEllipsis from "ac-components/ac-middle-ellipsis/ACMiddleEllipsis";
import ACPopover from "ac-components/ac-popover/ACPopover";

type TriggerRenderProps = {
  mode: AnyaSessionMode;
  selectedAgent: AnyaAgentSummary | null;
  selectedLabel: string;
};

type Props = {
  selectedAgent: AnyaAgentSummary | null;
  selectedMode?: AnyaSessionMode;
  onSelectAgent: (agent: AnyaAgentSummary) => void;
  onSelectChat?: () => void;
  agentOptions: AnyaAgentSummary[];
  searchAgents: (query: string) => CancellableAPIRequest<AnyaAgentSummary[]>;
  allowChatOption?: boolean;
  chatLabel?: string;
  emptyLabel?: string;
  menuWidth?: number;
  itemTextWidth?: number;
  placement?: "bottomLeft" | "bottomRight" | "topLeft" | "topRight";
  children: (props: TriggerRenderProps) => ReactElement;
};

type PopoverHelpers = {
  closePopover: () => void;
};

type SearchListUiState = Pick<
  ACAsyncSearchListState<AnyaAgentSummary>,
  "clearQuery"
>;

function isAnyaAgentSummary(
  agent: AnyaAgentSummary | null | undefined,
): agent is AnyaAgentSummary {
  return Boolean(agent);
}

function renderAgentLabel(agentName: string, itemTextWidth: number) {
  return (
    <span style={{ width: itemTextWidth, maxWidth: "100%" }}>
      <ACMiddleEllipsis text={agentName} applyWidth />
    </span>
  );
}

function renderActionRow({
  label,
  icon,
  secondaryContent,
  onClick,
}: {
  label: React.ReactNode;
  icon?: React.ReactNode;
  secondaryContent?: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <div
      className="d-flex align-items-center justify-content-between clickable w-100 font-size-md p-xs"
      onMouseDown={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
    >
      <div className="d-flex align-items-center flex-1 overflow-hidden">
        {icon ? (
          <span className="d-flex align-items-center m-r-xs">{icon}</span>
        ) : null}
        <span className="text-bold white-space-nowrap">{label}</span>
      </div>
      {secondaryContent ? (
        <span className="d-flex align-items-center m-l-xs">
          {secondaryContent}
        </span>
      ) : null}
    </div>
  );
}

const defaultListState: SearchListUiState = {
  clearQuery: async () => {},
};

const AnyaAgentSwitcher = memo(function AnyaAgentSwitcher({
  selectedAgent,
  selectedMode = AnyaSessionMode.AGENT,
  onSelectAgent,
  onSelectChat,
  agentOptions,
  allowChatOption = false,
  chatLabel = "Chat",
  emptyLabel = "Select agent",
  menuWidth = 250,
  itemTextWidth = 200,
  placement = "bottomLeft",
  children,
  searchAgents,
}: Props) {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [triggerWidth, setTriggerWidth] = useState(0);
  const [listState, setListState] =
    useState<SearchListUiState>(defaultListState);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLSpanElement | null>(null);

  const initialOptions = useMemo(
    () =>
      uniqBy(
        [selectedAgent, ...agentOptions].filter(isAnyaAgentSummary),
        (agent) => String(agent.id),
      ),
    [agentOptions, selectedAgent],
  );

  const fetchAgentOptions = useCallback(
    (query: string) => {
      const { promise, cancel } = searchAgents(query);
      return {
        promise: promise.then((results: AnyaAgentSummary[]) =>
          uniqBy(
            [selectedAgent, ...results].filter(isAnyaAgentSummary),
            (agent) => String(agent.id),
          ),
        ),
        cancel,
      };
    },
    [searchAgents, selectedAgent],
  );

  const handleSearchListStateChange = useCallback(
    (state: ACAsyncSearchListState<AnyaAgentSummary>) => {
      setListState((previousState) => {
        if (previousState.clearQuery === state.clearQuery) {
          return previousState;
        }

        return {
          clearQuery: state.clearQuery,
        };
      });
    },
    [],
  );

  const handleOptionClick = useCallback(
    (agent: AnyaAgentSummary, closePopover: () => void) => {
      onSelectAgent(agent);
      closePopover();
    },
    [onSelectAgent],
  );

  const closePopover = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      const focusableElement = contentRef.current?.querySelector<HTMLElement>(
        'input, button, [href], select, textarea, [tabindex]:not([tabindex="-1"])',
      );

      focusableElement?.focus();
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [open]);

  useEffect(() => {
    const triggerElement = triggerRef.current;

    if (!triggerElement) {
      return;
    }

    const updateTriggerWidth = () => {
      setTriggerWidth(triggerElement.getBoundingClientRect().width);
    };

    updateTriggerWidth();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateTriggerWidth();
    });

    observer.observe(triggerElement);

    return () => observer.disconnect();
  }, [children, emptyLabel, menuWidth, selectedAgent, selectedMode, chatLabel]);

  const selectedLabel =
    selectedMode === AnyaSessionMode.CHAT
      ? chatLabel
      : selectedAgent?.name || emptyLabel;

  const renderContent = useCallback(
    ({ closePopover }: PopoverHelpers) => (
      <div className="d-flex flex-column">
        {allowChatOption ? (
          <div className="d-flex flex-column">
            {renderActionRow({
              label: chatLabel,
              secondaryContent:
                selectedMode === AnyaSessionMode.CHAT ? (
                  <TickIcon />
                ) : undefined,
              onClick: () => {
                void listState.clearQuery();
                closePopover();
                onSelectChat?.();
              },
            })}
            <div className="m-y-xs border-bottom-light" />
          </div>
        ) : null}

        <ACAsyncSearchList
          fetchOptions={fetchAgentOptions}
          initialOptions={initialOptions}
          getOptionKey={(agent) => agent.id}
          isSelected={(agent) =>
            selectedMode === AnyaSessionMode.AGENT &&
            String(selectedAgent?.id) === String(agent.id)
          }
          renderOption={(agent, state) => (
            <div className="d-flex align-items-center justify-content-between w-100 p-xs clickable font-size-md">
              <div className="flex-1" style={{ minWidth: 0 }}>
                {renderAgentLabel(agent.name, itemTextWidth)}
              </div>
              {state.selected ? (
                <span className="d-flex align-items-center m-l-xs">
                  <TickIcon />
                </span>
              ) : null}
            </div>
          )}
          onSelect={(agent) => handleOptionClick(agent, closePopover)}
          emptyText={({ query }) =>
            query.trim() ? "No match found" : "No agents found"
          }
          onStateChange={handleSearchListStateChange}
        />

        <div className="d-flex flex-column">
          <div className="m-y-xs border-bottom-light" />
          {renderActionRow({
            label: "Manage Agents",
            icon: <SettingsIcon className="color-supporting" />,
            onClick: () => {
              void listState.clearQuery();
              closePopover();
              history.push("/automations/anya-agents");
            },
          })}
        </div>
      </div>
    ),
    [
      allowChatOption,
      chatLabel,
      fetchAgentOptions,
      handleOptionClick,
      handleSearchListStateChange,
      history,
      initialOptions,
      itemTextWidth,
      listState,
      onSelectChat,
      selectedAgent,
      selectedMode,
    ],
  );

  const trigger = useMemo(
    () =>
      children({
        mode: selectedMode,
        selectedAgent,
        selectedLabel,
      }),
    [children, selectedAgent, selectedLabel, selectedMode],
  );

  const popoverWidth = Math.max(menuWidth, triggerWidth);

  const popoverContent = useMemo(
    () => (
      <div
        ref={contentRef}
        className="d-flex flex-column p-y-xs"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            closePopover();
            event.preventDefault();
            event.stopPropagation();
          }
        }}
      >
        {renderContent({ closePopover })}
      </div>
    ),
    [closePopover, renderContent],
  );

  return (
    <ACPopover
      className="anya-agent-switcher-popover"
      content={popoverContent}
      popupVisible={open}
      onPopupVisibleChange={setOpen}
      placement={placement}
      style={{ width: popoverWidth, minWidth: popoverWidth }}
    >
      <span
        ref={triggerRef}
        className="d-inline-block"
        style={{ maxWidth: "100%" }}
      >
        {trigger}
      </span>
    </ACPopover>
  );
});

export default AnyaAgentSwitcher;
